import { Check, CircleAlert, Loader2, Mic, SkipForward, Sparkles, X } from 'lucide-react';
import type { z } from 'zod';
import type { AnswerFeedback } from '../../shared/answers.ts';
import type { speakingTask } from '../../shared/registry/speaking-task.ts';
import type { SpeechScores } from '../../shared/scoring.ts';
import type { TaskKind } from '../../shared/tasks.ts';
import { cn } from '../lib/cn.ts';
import type { CanvasItem, TaskView } from '../session/conversation.ts';
import { MIN_VOICED_MS } from '../voice/answer-recorder.ts';
import { SpeechScoresView } from './SpeechScoresView.tsx';

type Props = z.output<typeof speakingTask.params> & {
  task?: CanvasItem['task'];
  live?: TaskView | null;
  onDone?: () => void;
  onSkip?: () => void;
};

const LABEL: Record<TaskKind, string> = {
  interview_answer: 'Interview question',
  free_speech: 'Speak freely',
  read_aloud: 'Read aloud',
};
const READY: Record<TaskKind, string> = {
  interview_answer: 'Answer out loud when you are ready.',
  free_speech: 'Talk for 30 to 60 seconds, whenever you are ready.',
  read_aloud: 'Read it out loud when you are ready.',
};

/**
 * The script split into runs, with the words the scorer marked weak flagged. A weak word is marked where it was read
 * (`at`, its place in the script); without a place, or when the scorer split words differently from the script, every
 * reading of it is marked. Matching ignores case, punctuation and the kind of apostrophe.
 */
export function markWeakWords(
  script: string,
  weak: Array<{ word: string; at?: number }>,
): Array<{ text: string; weak: boolean }> {
  const core = (w: string) =>
    w
      .toLowerCase()
      .replace(/’/g, "'")
      .replace(/[^\p{L}\p{N}']/gu, '')
      .replace(/^'+|'+$/g, '');
  const runs = script.split(/(\s+)/).filter(Boolean);
  const isWord = (r: string) => !/^\s+$/.test(r);
  const words = runs.flatMap((r, i) => (isWord(r) ? [i] : []));
  const placed = new Set<number>();
  const anywhere = new Set<string>();
  for (const w of weak) {
    const c = core(w.word);
    if (!c) continue;
    const i = w.at === undefined ? undefined : words[w.at];
    if (i !== undefined && core(runs[i] ?? '') === c) placed.add(i);
    else anywhere.add(c);
  }
  return runs.map((text, i) => ({ text, weak: placed.has(i) || (isWord(text) && anywhere.has(core(text))) }));
}

const fmt = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, '0')}`;

export function SpeakingTaskCard({ kind, prompt, script, task, live, onDone, onSkip }: Props) {
  const phase = task?.phase ?? 'listening';
  const listening = phase === 'listening' || phase === 'too_short';
  const recording = listening && live?.recording;
  const enough = (live?.voicedMs ?? 0) >= MIN_VOICED_MS[kind];

  return (
    <article
      data-testid="speaking-task"
      data-kind={kind}
      data-phase={phase}
      className="card space-y-4 ring-1 ring-accent/20"
    >
      <header className="flex items-start gap-3">
        <span className="mt-0.5 rounded-xl bg-accent/10 p-2 text-accent">
          <Mic className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-accent">{LABEL[kind]}</p>
          <h3 className="mt-0.5 text-base font-semibold leading-snug text-fg">{prompt}</h3>
        </div>
      </header>

      {kind === 'read_aloud' && script ? <Script script={script} scores={task?.scores} /> : null}

      {listening ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm" aria-live="polite">
            <span
              className={cn(
                'size-2.5 shrink-0 rounded-full',
                recording ? 'animate-pulse bg-bad' : phase === 'too_short' ? 'bg-warn' : 'bg-fg/25',
              )}
              aria-hidden
            />
            <span
              data-testid="task-status"
              className={cn(phase === 'too_short' && !(recording && enough) ? 'text-warn' : 'text-muted')}
            >
              {recording
                ? `Recording ${fmt(live?.totalMs ?? 0)}${enough ? '' : ' · keep going'}`
                : phase === 'too_short'
                  ? kind === 'free_speech'
                    ? 'A little more: carry on when you are ready, then tap Done.'
                    : "I didn't catch enough. Try answering again, a little longer."
                  : READY[kind]}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="task-done"
              onClick={onDone}
              disabled={!recording}
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-semibold transition',
                enough ? 'bg-fg text-bg' : 'bg-fg/10 text-muted',
                'disabled:opacity-40',
              )}
            >
              <Check className="size-4" /> Done
            </button>
            <button
              type="button"
              data-testid="task-skip"
              onClick={onSkip}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-subtle ring-1 ring-fg/10 transition hover:text-fg"
            >
              <SkipForward className="size-4" /> Skip
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'processing' ? (
        <p className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Reviewing your answer…
        </p>
      ) : null}
      {phase === 'error' ? (
        <p className="flex items-center gap-2 text-sm text-bad">
          <CircleAlert className="size-4" aria-hidden /> Couldn't review that one. Let's keep going.
        </p>
      ) : null}
      {/* Only a tapped Skip is a skip; a card Gemini cancelled, or one open when the call ended, wasn't answered. */}
      {phase === 'skipped' ? (
        <p className="text-sm text-subtle">{task?.skippedByUser ? 'Skipped.' : 'Not answered.'}</p>
      ) : null}
      {phase === 'done' && task?.feedback ? (
        <AnswerFeedbackView feedback={task.feedback} scores={task.scores} pending={task.scoresPending} kind={kind} />
      ) : null}
    </article>
  );
}

/** A read-aloud's script; once scored, the words to work on are underlined where they were read. */
function Script({ script, scores }: { script: string; scores?: SpeechScores }) {
  const runs = markWeakWords(script, scores?.status === 'scored' ? scores.weakWords : []);
  // Named in the caption too: an underline alone isn't announced by every screen reader.
  const named = [
    ...new Map(
      runs
        .filter((r) => r.weak)
        .map((r) => r.text.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
        .map((w) => [w.toLowerCase(), w] as const),
    ).values(),
  ];
  return (
    <div className="space-y-1.5">
      <blockquote
        data-testid="read-aloud-script"
        className="rounded-2xl bg-fg/[0.04] px-4 py-3 text-[17px] leading-relaxed text-fg/90"
      >
        {runs.map((r, i) =>
          r.weak ? (
            <mark
              // biome-ignore lint/suspicious/noArrayIndexKey: runs are fixed for a given script
              key={i}
              data-testid="weak-word"
              className="bg-transparent text-fg underline decoration-warn decoration-wavy decoration-2 underline-offset-4"
            >
              {r.text}
            </mark>
          ) : (
            r.text
          ),
        )}
      </blockquote>
      {named.length ? (
        <p data-testid="weak-words" className="px-1 text-xs text-subtle">
          Give {named.length === 1 ? 'this' : 'these'} another go: {named.join(', ')}.
        </p>
      ) : null}
    </div>
  );
}

const STAR: Array<[keyof NonNullable<AnswerFeedback['star']>, string]> = [
  ['situation', 'Situation'],
  ['task', 'Task'],
  ['action', 'Action'],
  ['result', 'Result'],
];

export function AnswerFeedbackView({
  feedback,
  scores,
  pending,
  kind,
}: {
  feedback: AnswerFeedback;
  scores?: SpeechScores;
  pending?: boolean;
  kind: TaskKind;
}) {
  if (feedback.relevance === 'no_speech') {
    return <p className="text-sm text-subtle">I couldn't hear an answer in that recording.</p>;
  }
  return (
    <div data-testid="answer-feedback" className="space-y-4 border-t border-fg/10 pt-4">
      {feedback.unreviewed ? (
        <p data-testid="review-unavailable" className="text-[15px] text-muted">
          I couldn't review what you said this time. Your speech scores are below.
        </p>
      ) : null}
      {kind === 'interview_answer' && feedback.star ? (
        <ul className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" aria-label="STAR structure">
          {STAR.map(([k, label]) => (
            <li
              key={k}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ring-1',
                // The missing part is the coaching point, so it gets the warn tone, not the quietest one.
                feedback.star?.[k] ? 'bg-good/10 text-good ring-good/30' : 'bg-warn/10 text-warn ring-warn/30',
              )}
            >
              {feedback.star?.[k] ? <Check className="size-3" /> : <X className="size-3" />} {label}
            </li>
          ))}
        </ul>
      ) : null}
      {feedback.strengths.length ? (
        <section>
          <h4 className="text-xs font-medium uppercase tracking-wide text-good">What worked</h4>
          <ul className="mt-1.5 space-y-1 text-[15px] text-fg/85">
            {feedback.strengths.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {feedback.improve.length ? (
        <section>
          <h4 className="text-xs font-medium uppercase tracking-wide text-warn">Try next time</h4>
          <ul className="mt-1.5 space-y-1 text-[15px] text-fg/85">
            {feedback.improve.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {feedback.betterLine ? (
        <p className="flex gap-2 rounded-2xl bg-accent/[0.07] px-4 py-3 text-[15px] text-fg/90">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
          <span>“{feedback.betterLine}”</span>
        </p>
      ) : null}
      {/* After the review, which lands first: scores that stream in don't push what they're reading. */}
      {scores ? (
        <SpeechScoresView scores={scores} />
      ) : pending ? (
        <p data-testid="scores-pending" className="flex items-center gap-2 text-sm text-subtle">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Scoring your speech…
        </p>
      ) : null}
      {feedback.transcript ? (
        <details className="text-sm text-subtle">
          <summary className="cursor-pointer select-none">What I heard</summary>
          <p className="mt-2 text-muted">{feedback.transcript}</p>
        </details>
      ) : null}
    </div>
  );
}
